import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useDeleteUser,
  useInviteUser,
  useSetUserEnabled,
  useSetUserRole,
  useUsers,
} from '../api/hooks';
import { ErrorNote, PageHeader, Spinner } from '../components/Layout';
import { SectionHeader } from '../components/ui/primitives';
import { errorMessage, useToast } from '../components/ui/Toast';

export function UsersPage() {
  const { user } = useAuth();
  const users = useUsers();
  const invite = useInviteUser();
  const setRole = useSetUserRole();
  const setEnabled = useSetUserEnabled();
  const remove = useDeleteUser();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setNewRole] = useState<'admin' | 'editor'>('editor');
  const toast = useToast();

  return (
    <div className="page">
      <PageHeader title="Users" />

      <section className="card">
        <SectionHeader
          title="Invite someone"
          description="Cognito emails a temporary password and forces a reset on first sign-in. There is no public sign-up — this is the only way an account is created."
        />
        <div className="row">
          <input
            className="input"
            type="email"
            placeholder="name@ekkanoo.com.bh"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            className="input"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <select
            className="input select"
            value={role}
            onChange={(e) => setNewRole(e.target.value as 'admin' | 'editor')}
          >
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="button"
            className="btn primary"
            data-loading={invite.isPending || undefined}
            disabled={!email.includes('@') || !name.trim() || invite.isPending}
            onClick={() =>
              invite.mutate(
                { email: email.trim(), name: name.trim(), role },
                {
                  onSuccess: () => {
                    setEmail('');
                    setName('');
                    toast.success(`Invitation sent to ${email.trim()}`);
                  },
                  onError: (err) => toast.error(errorMessage(err)),
                },
              )
            }
          >
            Send invite
          </button>
        </div>
        {invite.error ? <ErrorNote error={invite.error} /> : null}
        <p className="field-hint">
          <strong>Editor</strong> creates and edits content and can publish.{' '}
          <strong>Admin</strong> additionally manages users, deletes content and rolls back a
          publish.
        </p>
      </section>

      <section className="card">
        <SectionHeader title="Accounts" />
        {users.isLoading ? <Spinner label="Loading users…" /> : null}
        {users.error ? <ErrorNote error={users.error} /> : null}

        <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>State</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((row) => {
              const isSelf = row.username === user?.email || row.username === user?.sub;
              return (
                <tr key={row.username}>
                  <td>
                    {row.name || <span className="muted">—</span>}
                    {isSelf ? <span className="badge role">you</span> : null}
                  </td>
                  <td className="mono">{row.email}</td>
                  <td>
                    <select
                      className="input select compact"
                      value={row.roles.includes('admin') ? 'admin' : 'editor'}
                      disabled={isSelf || setRole.isPending}
                      onChange={(e) =>
                        setRole.mutate({
                          username: row.username,
                          role: e.target.value as 'admin' | 'editor',
                        })
                      }
                    >
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td>
                    <span className={`badge ${row.enabled ? 'live' : 'draft'}`}>
                      {row.enabled ? row.status.replace(/_/g, ' ').toLowerCase() : 'disabled'}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn subtle"
                      disabled={isSelf || setEnabled.isPending}
                      onClick={() =>
                        setEnabled.mutate({ username: row.username, enabled: !row.enabled })
                      }
                    >
                      {row.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="btn subtle danger"
                      disabled={isSelf || remove.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Permanently delete ${row.email}? Disabling instead keeps the audit trail intact.`,
                          )
                        ) {
                          remove.mutate(row.username, {
                            onSuccess: () => toast.success(`Deleted ${row.email}`),
                            onError: (err) => toast.error(errorMessage(err)),
                          });
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {setRole.error ? <ErrorNote error={setRole.error} /> : null}
        {remove.error ? <ErrorNote error={remove.error} /> : null}
      </section>
    </div>
  );
}
